import { useState, useEffect, useMemo } from 'react';
import { NotificationsAPI, UsersAPI } from '../api';
import { Bell, Send, Trash2 } from 'lucide-react';
import { useAdmin } from '../lib/hooks';
import { Pagination } from './Pagination';
import { RichEditor } from './RichEditor';
import { SafeHtml } from './SafeHtml';

const AdminNotifications = () => {
    const { isAdmin, loading: adminLoading } = useAdmin();

    const [users, setUsers] = useState<any[]>([]);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Pagination & Sorting
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'createdAt', direction: 'desc' });

    // Form state
    const [notifUserId, setNotifUserId] = useState<string>('ALL');
    const [notifTitle, setNotifTitle] = useState('');
    const [notifMessage, setNotifMessage] = useState('');
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifResult, setNotifResult] = useState<{ ok: boolean; text: string } | null>(null);

    const loadData = async () => {
        if (!isAdmin) return;
        setLoading(true);
        try {
            const [usersRes, notifsRes] = await Promise.all([
                UsersAPI.getAll(),
                NotificationsAPI.getAll()
            ]);

            if (usersRes.success && usersRes.data) {
                setUsers(usersRes.data);
            }
            if (notifsRes.success && notifsRes.data) {
                // Return all to show soft-deleted status
                setNotifications(notifsRes.data);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [isAdmin]);

    const onDelete = async (id: string, isDeleted: boolean) => {
        if (isDeleted) {
            if (!confirm('¿Estás seguro de eliminar PERMANENTEMENTE esta notificación? Esta acción no se puede deshacer.')) return;
            await NotificationsAPI.hardDelete(id);
        } else {
            if (!confirm('¿Estás seguro de eliminar esta notificación?')) return;
            await NotificationsAPI.delete(id);
        }
        await loadData();
    };

    const formatDate = (dateValue: string | Date | null) => {
        if (!dateValue) return '';
        const d = new Date(dateValue);
        return d.toLocaleDateString('es-UY', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getUserLabel = (id: string) => {
        const u = users.find(user => user.id === id);
        if (!u) return 'Desconocido';
        return u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email;
    };

    const sortedNotifications = useMemo(() => {
        const sortable = [...notifications];
        if (sortConfig !== null) {
            sortable.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'createdAt') {
                    aValue = aValue ? new Date(aValue).getTime() : 0;
                    bValue = bValue ? new Date(bValue).getTime() : 0;
                } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [notifications, sortConfig]);

    const onSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>;
    if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>;

    const total = sortedNotifications.length;
    const startIdx = (page - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, total);
    const pageItems = sortedNotifications.slice(startIdx, endIdx);

    const handleSendNotification = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!notifTitle.trim() || !notifMessage.trim()) return;
        setNotifLoading(true);
        setNotifResult(null);
        try {
            const targets = notifUserId === 'ALL'
                ? users.map((u) => u.id)
                : [notifUserId];

            const errors: string[] = [];
            for (const uid of targets) {
                const res = await NotificationsAPI.create(uid, notifTitle.trim(), notifMessage.trim());
                if (!res.success) errors.push(uid);
            }
            if (errors.length === 0) {
                setNotifResult({ ok: true, text: `Notificación enviada a ${targets.length} usuario(s) correctamente.` });
                setNotifTitle('');
                setNotifMessage('');
                await loadData();
            } else {
                setNotifResult({ ok: false, text: `Error al enviar a ${errors.length} usuario(s).` });
            }
        } finally {
            setNotifLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-16">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    Administrar Notificaciones
                </h2>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 mb-8">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-500" />
                    <h3 className="text-lg font-semibold dark:text-gray-100">Enviar Notificación</h3>
                </div>
                <div className="px-5 py-5">
                    <form onSubmit={handleSendNotification} className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destinatario</label>
                                <select
                                    value={notifUserId}
                                    onChange={(e) => setNotifUserId(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                >
                                    <option value="ALL">Todos los usuarios</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título</label>
                                <input
                                    type="text"
                                    value={notifTitle}
                                    onChange={(e) => setNotifTitle(e.target.value)}
                                    placeholder="Ej: Mantenimiento programado"
                                    required
                                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-gray-400"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mensaje</label>
                            <RichEditor
                                value={notifMessage}
                                onChange={setNotifMessage}
                                placeholder="Escribí el mensaje completo (admite HTML)..."
                            />
                        </div>
                        {notifResult && (
                            <p className={`text-sm font-medium ${notifResult.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {notifResult.text}
                            </p>
                        )}
                        <div>
                            <button
                                type="submit"
                                disabled={notifLoading || !notifTitle.trim() || !notifMessage.trim()}
                                className="flex items-center gap-2 px-5 py-2 rounded-md bg-blue-600 text-white font-medium shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                                {notifLoading ? 'Enviando...' : 'Enviar Notificación'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold dark:text-gray-100">Registro de Notificaciones</h3>
                    <div className="text-sm text-gray-500 mt-1">Mostrando {total === 0 ? 0 : startIdx + 1}-{endIdx} de {total} notificaciones previas</div>
                </div>
                {loading ? (
                    <div className="px-5 py-8 text-gray-500">Cargando...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead className="bg-gray-50 dark:bg-slate-700/50">
                                <tr>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase p-3 cursor-pointer group" onClick={() => onSort('userId')}>
                                        <div className="flex items-center gap-1">Destinatario</div>
                                    </th>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase p-3 cursor-pointer group" onClick={() => onSort('title')}>
                                        <div className="flex items-center gap-1">Título</div>
                                    </th>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase p-3 cursor-pointer group" onClick={() => onSort('createdAt')}>
                                        <div className="flex items-center gap-1">Fecha</div>
                                    </th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase p-3">Estado</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase p-3">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                                {pageItems.map((n) => (
                                    <tr key={n.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                                        <td className="p-3 text-sm text-gray-700 dark:text-gray-300">
                                            {getUserLabel(n.userId)}
                                        </td>
                                        <td className="p-3 text-sm text-gray-900 dark:text-gray-100 font-medium">
                                            {n.title}
                                            <SafeHtml
                                                html={n.message}
                                                mode="rich"
                                                className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5 prose prose-sm max-w-sm dark:prose-invert [&>p]:mb-1 [&>p:last-child]:mb-0 [&>ul]:my-1 [&>ol]:my-1"
                                            />
                                        </td>
                                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                                            {formatDate(n.createdAt)}
                                        </td>
                                        <td className="p-3 text-center">
                                            <div className="flex flex-col items-center gap-1 text-xs">
                                                {n.deletedAt ? (
                                                    <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Borrada</span>
                                                ) : (
                                                    <>
                                                        {n.isRead ? (
                                                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Leída</span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">No leída</span>
                                                        )}
                                                        {n.isArchived && (
                                                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Archivada</span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => onDelete(n.id, !!n.deletedAt)}
                                                className={`p-1.5 rounded-full transition-colors ${n.deletedAt
                                                        ? 'text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/40 dark:hover:bg-red-900/60'
                                                        : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30'
                                                    }`}
                                                title={n.deletedAt ? "Eliminar Permanentemente" : "Eliminar"}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {pageItems.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="p-5 text-center text-sm text-gray-500 dark:text-gray-400">No hay notificaciones creadas.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                {!loading && total > 0 && (
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalItems={total}
                        className="bg-gray-50 dark:bg-slate-900/50 rounded-b-xl"
                    />
                )}
            </div>
        </div>
    );
};

export default AdminNotifications;
